import { createSelector } from 'reselect';
import { v4 as uuidv4 } from 'uuid';
import {
  DeviceQuantityMap,
  OBAvailableEndpoints,
  OBAvailableHardware,
  OBAvailableVirtualSensors,
  OBCloudSensors,
  OBContact,
  OBContacts,
  OBEndpointConfig,
  OBGenericSensor,
  OBIdToContactMap,
  OBIdToScannerMap,
  OBIdToSensorMap,
  OBIdToSiteMap,
  OBInternetExposure,
  OBProducts,
  OBScanner,
  OBScanners,
  OBSensor,
  OBSensors,
  OBServiceTypes,
  OBSite,
  OBSites,
  OBSyncResults,
  OBTeamContacts,
  OBVirtualizationSoftware,
  OBVirtualScanner,
  OBVirtualSensorConfig,
  OptionType,
  PartnerTypeEnum,
  SensorCapabilities,
  SensorCapability,
  Store,
} from '../../types';
import { OnboardingAction } from '../../actions/onboarding';
import onboardingUtils from './utils';
import {
  EDR_SKUS,
  IRR_SKUS,
  LEGACY_CST_TIER_SKUS,
  MA_SKUS,
  MSP_SUFFIX,
} from '../../constants';
import { featureFlagSelector, sessionSelector } from '../session';
import { onboardingSiteReducer } from './sites';
import { AWN_202_SCANNER_INFO } from '../../routes/onboarding/site-config/v2/scanner/constants';

export type State = {
  readonly contactsSection: OBContacts;
  readonly lastUpdatedContacts: OBIdToContactMap;
  readonly internetExposure: OBInternetExposure;
  readonly sitesSection: OBSites;
  readonly sensorsSection: OBSensors;
  readonly cloudSensors: OBCloudSensors;
  readonly scannersSection: OBScanners;
  readonly virtualScanner: OBVirtualScanner;
  readonly endpointSection: OBEndpointConfig;
  readonly virtualSensorSection: OBVirtualSensorConfig;
  readonly onboardingTeamContacts: OBTeamContacts;
  readonly syncResults: OBSyncResults | null;
  readonly syncModalOpen: boolean;
  readonly products: OBProducts;
  readonly availableHardware: OBAvailableHardware;
  readonly availableEndpoints: OBAvailableEndpoints;
  readonly availableVirtualSensors: OBAvailableVirtualSensors;
  readonly availableGenericSensors: OBGenericSensor[];
  readonly sensorCapabilities: Record<string, SensorCapability[]>;
};

/**
 * node names of the sections that have a finalization status
 */
enum OB_FINALIZABLE_SECTION {
  CONTACT = 'contactsSection',
  SITE = 'sitesSection',
  CLOUD_SENSORS = 'cloudSensors',
  INTERNET_EXPOSURE = 'internetExposure',
  ENDPOINT = 'endpointSection',
}

export const DEFAULT_INTERNET_EXPOSURE: OBInternetExposure = {
  ipAddresses: '',
  dnsDomains: '',
  vulScanIPsConfigured: false,
  noExternalHosts: false,
  domainOwnershipConfirmed: false,
  completed: false,
  finalized: null,
  invalidTargets: [],
};

export const DEFAULT_CLOUD_SENSORS: OBCloudSensors = {
  aws: {
    regions: [],
    cloudWatch: false,
    cloudTrail: false,
    guardDuty: false,
    acl: false,
    eks: false,
    completed: true,
  },
  azure: {
    logAnalytics: false,
    securityCenter: false,
    sentinel: false,
    completed: true,
  },
  o365: { licenses: [], dlp: false, completed: true },
  sf: { licenses: [], completed: true },
  geo: { countries: [], completed: false },
  gcp: { licenses: [] },
  imanage: { licenses: [], completed: true },
  finalized: null,
};

export const DEFAULT_VIRTUAL_SCANNER: OBVirtualScanner = {
  software: OBVirtualizationSoftware.NONE,
  requirementsMet: false,
  noVirtualScanners: false,
  completed: true,
};

const INITIAL_CONTACT_SECTION: OBContacts = {
  contacts: {},
  completed: false,
  finalized: null,
  loading: false,
};

const INITIAL_PRODUCTS: OBProducts = {
  services: [],
  hardwareQuantity: {},
  cloudSensors: [],
  virtualSensors: [],
};

export const DEFAULT_ENDPOINT_CONFIG: OBEndpointConfig = {
  cloudEpps: [],
  onPremEpps: [],
  notes: '',
  completed: false,
  noOnPremEpps: false,
  noCloudEpps: false,
  sysmonInstalled: false,
  windowsEndpoints: '0',
  macEndpoints: '0',
  linuxEndpoints: '0',
  agentCompleted: false,
  eppCompleted: false,
  noAgents: false,
  finalized: null,
};

export const DEFAULT_VIRTUAL_SENSOR_CONFIG: OBVirtualSensorConfig = {
  hypervisor: [],
  noVirtualSensor: false,
  virtualizationRequirements: true,
  completed: false,
};

export const DEFAULT_AVAILABLE_ENDPOINTS: OBAvailableEndpoints = {
  onPremiseEndpoints: [],
  cloudEndpoints: [],
};

export const DEFAULT_AVAILABLE_VIRTUAL_SENSORS: OBAvailableVirtualSensors = {
  sensors: [],
};

export const DEFAULT_AVAILABLE_GENERIC_SENSORS: OBGenericSensor[] = [];

// Placeholder until we have data to get from backend
// Optional(not purchased) sections will be in a completed state by default
export const INITIAL_ONBOARDING_STATE: State = {
  contactsSection: INITIAL_CONTACT_SECTION,
  lastUpdatedContacts: {},
  internetExposure: DEFAULT_INTERNET_EXPOSURE,
  sitesSection: {
    sites: {},
    completed: false,
    finalized: null,
    configureConnectWise: false,
  },
  sensorsSection: { sensors: {}, completed: true, loading: false },
  scannersSection: { scanners: {}, completed: true, loading: false },
  cloudSensors: DEFAULT_CLOUD_SENSORS,
  virtualScanner: DEFAULT_VIRTUAL_SCANNER,
  endpointSection: DEFAULT_ENDPOINT_CONFIG,
  syncResults: { success: [], errors: [] },
  onboardingTeamContacts: {},
  syncModalOpen: false,
  products: INITIAL_PRODUCTS,
  availableHardware: { sensors: [], scanners: [] },
  availableEndpoints: DEFAULT_AVAILABLE_ENDPOINTS,
  availableVirtualSensors: DEFAULT_AVAILABLE_VIRTUAL_SENSORS,
  virtualSensorSection: DEFAULT_VIRTUAL_SENSOR_CONFIG,
  availableGenericSensors: DEFAULT_AVAILABLE_GENERIC_SENSORS,
  sensorCapabilities: {},
};

const onboardingReducer = (
  state: State = INITIAL_ONBOARDING_STATE,
  action: OnboardingAction,
): State => {
  switch (action.type) {
    /**
     * Contacts
     */
    case 'set_contacts':
      const cs = onboardingUtils.contactsFromAPI(action.contacts);
      return {
        ...state,
        contactsSection: cs,
        lastUpdatedContacts: cs.contacts,
      };
    case 'set_contact':
      return {
        ...state,
        contactsSection: {
          ...state.contactsSection,
          contacts: {
            ...state.contactsSection.contacts,
            [action.uuid]: action.contact,
          },
        },
      };
    case 'set_contacts_loading':
      return {
        ...state,
        contactsSection: {
          ...state.contactsSection,
          loading: action.loading,
        },
      };
    case 'set_last_updated_contact':
      return {
        ...state,
        lastUpdatedContacts: {
          ...state.lastUpdatedContacts,
          [action.uuid]: action.contact,
        },
      };
    case 'revert_contacts':
      return {
        ...state,
        contactsSection: {
          ...state.contactsSection,
          contacts: { ...state.lastUpdatedContacts },
        },
      };

    /**
     * Internet Exposure
     */
    case 'edit_internet_exposure':
      return {
        ...state,
        internetExposure: action.internetExposure,
      };
    case 'set_internet_exposure':
      return {
        ...state,
        internetExposure: onboardingUtils.internetExposureFromAPI(
          action.internetExposure,
        ),
      };

    /**
     * Sites
     */
    case 'set_sites':
    case 'set_site':
      return {
        ...state,
        sitesSection: onboardingSiteReducer(state.sitesSection, action),
      };
    case 'delete_site':
      const sites = { ...state.sitesSection.sites };
      delete sites[action.uuid];
      return {
        ...state,
        sitesSection: {
          ...state.sitesSection,
          sites,
        },
      };
    /**
     * Sensor
     */
    case 'add_sensor':
      return {
        ...state,
        sensorsSection: {
          ...state.sensorsSection,
          sensors: {
            ...state.sensorsSection.sensors,
            [action.uuid]: action.sensor,
          },
        },
      };
    case 'edit_sensor':
      return {
        ...state,
        sensorsSection: {
          ...state.sensorsSection,
          sensors: {
            ...state.sensorsSection.sensors,
            [action.uuid]: action.sensor,
          },
        },
      };
    case 'set_sensors':
      return {
        ...state,
        sensorsSection: onboardingUtils.sensorsFromAPI(action.sensors),
      };
    case 'set_sensors_loading':
      return {
        ...state,
        sensorsSection: {
          ...state.sensorsSection,
          loading: action.loading,
        },
      };
    case 'delete_sensor':
      const sensors = { ...state.sensorsSection.sensors };
      delete sensors[action.uuid];
      return {
        ...state,
        sensorsSection: {
          ...state.sensorsSection,
          sensors,
        },
      };
    /**
     * Cloud Sensors
     */
    case 'set_cloud_sensors':
      return {
        ...state,
        cloudSensors: onboardingUtils.cloudSensorsFromAPI(action.cloudSensors),
      };
    case 'edit_cloud_sensors':
      return {
        ...state,
        cloudSensors: action.cloudSensors,
      };

    /**
     * Risk Scanners
     */
    case 'add_scanner':
      return {
        ...state,
        scannersSection: {
          ...state.scannersSection,
          scanners: {
            ...state.scannersSection.scanners,
            [uuidv4()]: action.scanner,
          },
        },
      };
    case 'edit_scanner':
      return {
        ...state,
        scannersSection: {
          ...state.scannersSection,
          scanners: {
            ...state.scannersSection.scanners,
            [action.uuid]: action.scanner,
          },
        },
      };
    case 'set_scanners':
      return {
        ...state,
        scannersSection: onboardingUtils.scannersFromAPI(action.scanners),
      };
    case 'delete_scanner':
      const scanners = { ...state.scannersSection.scanners };
      delete scanners[action.uuid];
      return {
        ...state,
        scannersSection: {
          ...state.scannersSection,
          scanners,
        },
      };
    case 'set_scanners_loading':
      return {
        ...state,
        scannersSection: {
          ...state.scannersSection,
          loading: action.loading,
        },
      };
    /**
     * Virtual Scanner
     */
    case 'edit_virtual_scanner':
      return {
        ...state,
        virtualScanner: action.setting,
      };
    case 'set_virtual_scanner':
      return {
        ...state,
        virtualScanner: onboardingUtils.virtualScannerFromAPI(action.setting),
      };
    /**
     * Virtual Sensor
     */
    case 'edit_virtual_sensor':
      return {
        ...state,
        virtualSensorSection: action.config,
      };
    case 'set_virtual_sensor':
      return {
        ...state,
        virtualSensorSection: onboardingUtils.virtualSensorConfigFromAPI(
          action.config,
        ),
      };
    case 'set_onboarding_available_virtual_sensors':
      return {
        ...state,
        availableVirtualSensors: onboardingUtils.availableVirtualSensorsFromAPI(
          action.availableVirtualSensors,
        ),
      };
    /**
     * Endpoint Config
     */
    case 'edit_endpoint':
      return {
        ...state,
        endpointSection: action.config,
      };
    case 'set_endpoint':
      return {
        ...state,
        endpointSection: onboardingUtils.endpointConfigFromAPI(action.config),
      };
    case 'set_onboarding_available_endpoints':
      return {
        ...state,
        availableEndpoints: onboardingUtils.availableEndpointsFromAPI(
          action.availableEndpoints,
        ),
      };
    case 'toggle_complete': {
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          completed: !state[action.key].completed,
        },
      };
    }
    case 'toggle_cloud_sensor_complete': {
      return {
        ...state,
        cloudSensors: {
          ...state.cloudSensors,
          [action.key]: {
            ...state.cloudSensors[action.key],
            completed: !state.cloudSensors[action.key].completed,
          },
        },
      };
    }
    case 'set_sync_results': {
      return {
        ...state,
        syncResults: action.results,
      };
    }
    case 'set_onboarding_team_contacts':
      return {
        ...state,
        onboardingTeamContacts: action.contacts,
      };
    case 'set_onboarding_sync_modal_open':
      return {
        ...state,
        syncModalOpen: action.open,
      };
    case 'set_onboarding_products':
      return {
        ...state,
        products: onboardingUtils.productsFromAPI(action.products),
      };
    case 'set_onboarding_available_hardware':
      return {
        ...state,
        availableHardware: action.availableHardware,
      };
    case 'set_onboarding_available_generic_sensors':
      return {
        ...state,
        availableGenericSensors: action.availableGenericSensors,
      };
    case 'set_onboarding_sensor_capabilities':
      return {
        ...state,
        sensorCapabilities: {
          ...state.sensorCapabilities,
          ...action.sensorCapabilities,
        },
      };
    case 'set_onboarding_available_hardware_quantity':
      return {
        ...state,
        products: {
          ...state.products,
          hardwareQuantity: {
            ...state.products.hardwareQuantity,
            ...action.hardwareQuantity,
          },
        },
      };
    default:
      return state;
  }
};

export default onboardingReducer;

const getPrimaryContact = (store: Store): OBContact | null =>
  Object.values(store.onboarding.contactsSection.contacts).filter(
    (contact) => contact.contactType === 'primary',
  )?.[0];

export const getFullName = (contact: OBContact | null) => {
  if (contact) {
    return `${contact.firstName} ${contact.lastName}`;
  }
  return '';
};

export const getContactOption = (
  uuid: string,
  contact: OBContact,
  disableMSP = true,
  requireEmail = false,
): OptionType => {
  const option: OptionType = {
    value: uuid,
    label: getFullName(contact),
  };
  if (contact.isMSP) {
    option.customElement = MSP_SUFFIX;
    option.isDisabled = disableMSP;
  }
  if (requireEmail) {
    option.isDisabled = option.isDisabled || !contact.email;
  }
  return option;
};

export const getPrimaryContactId = (
  contacts: OBIdToContactMap,
): string | undefined =>
  Object.keys(contacts).filter(
    (id) => contacts[id].contactType === 'primary',
  )[0];

const getSites = (store: Store): OBIdToSiteMap =>
  store.onboarding.sitesSection.sites;

const getContacts = (store: Store): OBIdToContactMap =>
  store.onboarding.contactsSection.contacts;

const getSensors = (store: Store): OBIdToSensorMap =>
  store.onboarding.sensorsSection.sensors;

const memoizedGetSiteNames = createSelector([getSites], (sites) => {
  return new Set<string>(Object.values(sites).map((site) => site.name));
});

const memoizedGetSensorNames = createSelector([getSensors], (sensors) => {
  return new Set<string>(Object.values(sensors).map((sensor) => sensor.name));
});

const memoizedGetContactsWithoutMSP = createSelector(
  [sessionSelector.getMSPID, sessionSelector.isAwnUser, getContacts],
  (mspID, isAwnUser, contacts) => {
    if (mspID || isAwnUser) {
      return contacts;
    } else {
      return Object.keys(contacts)
        .filter((id) => !contacts[id].isMSP)
        .reduce((result, id) => {
          result[id] = contacts[id];
          return result;
        }, {} as OBIdToContactMap);
    }
  },
);

const memoizedSessionUserContact = createSelector(
  [sessionSelector.getUserEmail, getContacts],
  (userEmail, contacts) => {
    return Object.values(contacts).find(
      (contact) => contact?.email === userEmail,
    );
  },
);

const hasPurchasedCloudSensors = (store: Store) =>
  store.onboarding.products.cloudSensors.length > 0;

const hasPurchasedVirtualSensors = (store: Store) =>
  store.onboarding.products.virtualSensors.length > 0;

const isSectionFinalized =
  (section: OB_FINALIZABLE_SECTION) => (store: Store) => {
    return !store.onboarding[section].finalized;
  };

const onlyPurchasedAddOnServices = (store: Store): boolean =>
  !store.onboarding.products.services.find(
    (s) =>
      !(
        MA_SKUS.includes(s) ||
        IRR_SKUS.includes(s) ||
        EDR_SKUS.includes(s) ||
        LEGACY_CST_TIER_SKUS.includes(s)
      ),
  ) &&
  (onboardingSelector.isManagedAwarenessEnabled(store) ||
    onboardingSelector.hasIRRProduct(store) ||
    onboardingSelector.hasEDRProduct(store));

const onlyPurchasedIRRProducts = (store: Store): boolean =>
  !store.onboarding.products.services.find((s) => !IRR_SKUS.includes(s)) &&
  onboardingSelector.hasIRRProduct(store);

export const onboardingSelector = {
  /**
   * Contacts
   */
  getContactSection: (store: Store): OBContacts =>
    store.onboarding.contactsSection,
  hasFetchedContactSection: (store: Store): boolean =>
    store.onboarding.contactsSection !== INITIAL_CONTACT_SECTION,
  getContactSectionLoading: (store: Store): boolean =>
    store.onboarding.contactsSection.loading,
  getSitesSection: (store: Store): OBSites => store.onboarding.sitesSection,
  getContactSectionConfigureConnectWise: (store: Store): boolean =>
    store.onboarding.sitesSection.configureConnectWise,
  getContacts,
  getContactsWithoutMSP: memoizedGetContactsWithoutMSP,
  getContactByUuid:
    (uuid: string) =>
    (store: Store): OBContact | undefined =>
      store.onboarding.contactsSection.contacts[uuid],
  getPrimaryContact: getPrimaryContact,
  getPrimaryContactName: (store: Store): string => {
    const contact = getPrimaryContact(store);
    return getFullName(contact);
  },
  getContactsCompleted: (store: Store): boolean =>
    store.onboarding.contactsSection.completed,
  isPrimaryContactSelected: (store: Store): boolean =>
    !!getPrimaryContact(store),
  areEscalationsSelected: (store: Store): boolean => {
    const contactsDetails = Object.values(
      onboardingSelector.getContacts(store),
    );
    return contactsDetails.some((c) => c.isBasicEscalationContact);
  },
  isExecutiveSponsorSelected: (store: Store): boolean => {
    // Do not require executive sponsor only if customer is part of a MSP deal
    if (sessionSelector.getCustomerPartnerType(store) === PartnerTypeEnum.MSP) {
      return true;
    }
    const contactsDetails = Object.values(
      onboardingSelector.getContacts(store),
    );
    return contactsDetails.some((c) => c.isExecutiveSponsor);
  },
  areImportantContactsSelected: (store: Store): boolean =>
    onboardingSelector.isPrimaryContactSelected(store) &&
    onboardingSelector.isExecutiveSponsorSelected(store),
  areReportRecipientsSelected: (store: Store): boolean => {
    const contactsDetails = Object.values(
      onboardingSelector.getContacts(store),
    );
    return contactsDetails.some((c) => c.isReportRecipient);
  },
  areAccessControlsSelected: (store: Store): boolean => {
    const contactsDetails = Object.values(
        onboardingSelector.getContacts(store),
      ),
      isManagedRiskEnabled = onboardingSelector.isManagedRiskEnabled(store),
      isLogSearchEnabled = onboardingSelector.isLogSearchEnabled(store);

    return (
      contactsDetails.some((c) => c.isPortalUser) &&
      (!isLogSearchEnabled || contactsDetails.some((c) => c.isLogSearchUser)) &&
      (!isManagedRiskEnabled || contactsDetails.some((c) => c.isRiskUser))
    );
  },

  /**
   * Internet Exposore
   */
  getInternetExposure: (store: Store): OBInternetExposure =>
    store.onboarding.internetExposure,

  getInternetExposureCompleted: (store: Store): boolean =>
    store.onboarding.internetExposure.completed,

  /**
   * Sites
   */
  getSites,
  getSiteByUuid:
    (uuid: string) =>
    (store: Store): OBSite | undefined =>
      store.onboarding.sitesSection.sites[uuid],
  getSitesCompleted: (store: Store): boolean =>
    store.onboarding.sitesSection.completed,

  getSiteConfigCompleted: (store: Store): boolean =>
    store.onboarding.sitesSection.completed &&
    store.onboarding.sensorsSection.completed &&
    store.onboarding.scannersSection.completed &&
    store.onboarding.virtualScanner.completed,

  getSiteNames: memoizedGetSiteNames,
  hasFetchedSiteSection: (store: Store): boolean =>
    store.onboarding.sitesSection !== INITIAL_ONBOARDING_STATE.sitesSection,

  /**
   * Sensors
   */
  getSensorsSection: (store: Store): OBSensors =>
    store.onboarding.sensorsSection,
  getSensors,
  getSensorNames: memoizedGetSensorNames,
  getSensorByUuid:
    (uuid: string) =>
    (store: Store): OBSensor | undefined =>
      store.onboarding.sensorsSection.sensors[uuid],
  getSensorsCompleted: (store: Store): boolean =>
    store.onboarding.sensorsSection.completed,
  getSensorsSectionLoading: (store: Store): boolean =>
    store.onboarding.sensorsSection.loading,
  hasFetchedSensorSection: (store: Store): boolean =>
    store.onboarding.sensorsSection !== INITIAL_ONBOARDING_STATE.sensorsSection,
  showGenericSeries: (store: Store): boolean =>
    !Object.values(store.onboarding.sensorsSection.sensors)
      .filter(
        (sensor) =>
          !store.onboarding.sitesSection.sites[sensor.siteId]?.finalized,
      ) //only check unfinalized sensors
      .find((sensor) => !sensor.series),
  getSensorCapabilities: (store: Store): SensorCapabilities =>
    store.onboarding.sensorCapabilities,

  /**
   * Cloud Sensors
   */
  getCloudSensors: (store: Store): OBCloudSensors =>
    store.onboarding.cloudSensors,

  getCloudSensorConfigCompleted: (store: Store): boolean =>
    store.onboarding.cloudSensors.aws.completed &&
    store.onboarding.cloudSensors.azure.completed &&
    store.onboarding.cloudSensors.o365.completed &&
    store.onboarding.cloudSensors.sf.completed &&
    store.onboarding.cloudSensors.geo.completed &&
    store.onboarding.cloudSensors.imanage.completed,

  /**
   * Risk Scanners
   */
  getScanners: (store: Store): OBIdToScannerMap =>
    store.onboarding.scannersSection.scanners,
  getScannerByUuid:
    (uuid: string) =>
    (store: Store): OBScanner | undefined =>
      store.onboarding.scannersSection.scanners[uuid],
  getScannersCompleted: (store: Store): boolean =>
    store.onboarding.scannersSection.completed,
  getScannersSectionLoading: (store: Store): boolean =>
    store.onboarding.scannersSection.loading,
  hasFetchedScannerSection: (store: Store): boolean =>
    store.onboarding.scannersSection !==
    INITIAL_ONBOARDING_STATE.scannersSection,

  /**
   * Virtual Scanner
   */
  getVirtualScanner: (store: Store): OBVirtualScanner =>
    store.onboarding.virtualScanner,

  /**
   * Endpoint Config
   */
  getEndpointConfig: (store: Store): OBEndpointConfig =>
    store.onboarding.endpointSection,
  getEndpointConfigCompleted: (store: Store): boolean =>
    store.onboarding.endpointSection.completed,
  getAvailableEndpoints: (store: Store): OBAvailableEndpoints =>
    store.onboarding.availableEndpoints,

  /**
   * Virtual Sensor Config
   */
  getVirtualSensorConfig: (store: Store): OBVirtualSensorConfig =>
    store.onboarding.virtualSensorSection,
  getVirtualSensorConfigCompleted: (store: Store): boolean =>
    store.onboarding.virtualSensorSection.completed,
  getAvailableVirtualSensors: (store: Store): OBAvailableVirtualSensors =>
    store.onboarding.availableVirtualSensors,

  /**
   * Others
   */

  getOnboardingTeamContacts: (store: Store): OBTeamContacts =>
    store.onboarding.onboardingTeamContacts,

  getIsSyncModalOpen: (store: Store): boolean => store.onboarding.syncModalOpen,

  getSyncResults: (store: Store): OBSyncResults | null =>
    store.onboarding.syncResults,

  hasFetchedOnboardingProducts: (store: Store): boolean =>
    store.onboarding.products !== INITIAL_PRODUCTS,

  getOnboardingProducts: (store: Store): OBProducts =>
    store.onboarding.products,

  hasPurchasedCloudSensors,

  hasPurchasedVirtualSensors,

  isSessionUserPrimaryOrSecondaryContact: (store: Store): boolean => {
    const sessionUserContact = memoizedSessionUserContact(store);
    return (
      sessionUserContact?.contactType === 'primary' ||
      sessionUserContact?.contactType === 'secondary'
    );
  },

  isManagedRiskEnabled: (store: Store): boolean =>
    store.onboarding.products.services.includes(OBServiceTypes.managedRisk),

  hasCSPM: (store: Store): boolean =>
    store.onboarding.products.services.includes(OBServiceTypes.cspmAzure) ||
    store.onboarding.products.services.includes(OBServiceTypes.cspmAws) ||
    store.onboarding.products.services.includes(OBServiceTypes.cspmGcp),

  isManagedDetectionEnabled: (store: Store): boolean =>
    store.onboarding.products.services.includes(
      OBServiceTypes.managedDetection,
    ),

  isLogSearchEnabled: (store: Store): boolean =>
    store.onboarding.products.services.includes(OBServiceTypes.logSearch) ||
    store.onboarding.products.services.includes(OBServiceTypes.dataExploration),

  isManagedAwarenessEnabled: (store: Store): boolean =>
    !!store.onboarding.products.services.find((s) => MA_SKUS.includes(s)),

  hasIRRProduct: (store: Store): boolean =>
    !!store.onboarding.products.services.find((s) => IRR_SKUS.includes(s)),

  hasEDRProduct: (store: Store): boolean =>
    !!store.onboarding.products.services.find((s) => EDR_SKUS.includes(s)),

  isIRJSEnabled: (store: Store): boolean =>
    !!store.onboarding.products.services.find(
      (s) => s === OBServiceTypes.irJumpstartRetainer,
    ),

  isJumpStartEnabled: (store: Store): boolean =>
    !!store.onboarding.products.services.find(
      (s) => s === OBServiceTypes.jumpStartRetainer,
    ),

  isIncident360Enabled: (store: Store): boolean =>
    !!store.onboarding.products.services.find(
      (s) => s === OBServiceTypes.incident360,
    ),

  isIncident360PlusEnabled: (store: Store): boolean =>
    !!store.onboarding.products.services.find(
      (s) => s === OBServiceTypes.incident360Plus,
    ),

  isRapidResponseEnabled: (store: Store): boolean =>
    !!store.onboarding.products.services.find(
      (s) => s === OBServiceTypes.rapidResponse,
    ),

  getAvailableHardware: (store: Store): OBAvailableHardware => {
    return store.onboarding.availableHardware;
  },

  getAvailableGenericSensors: (store: Store): OBGenericSensor[] =>
    store.onboarding.availableGenericSensors,

  hasFetchedAvailableHardware: (store: Store): boolean =>
    store.onboarding.availableHardware !==
    INITIAL_ONBOARDING_STATE.availableHardware,

  hasFetchedAvailableGenericSensors: (store: Store): boolean =>
    store.onboarding.availableGenericSensors !==
    INITIAL_ONBOARDING_STATE.availableGenericSensors,

  getSectionVisibility: {
    contacts: isSectionFinalized(OB_FINALIZABLE_SECTION.CONTACT),
    sites: (store: Store) =>
      isSectionFinalized(OB_FINALIZABLE_SECTION.SITE)(store) &&
      !onlyPurchasedAddOnServices(store),
    cloudSensors: (store: Store) =>
      isSectionFinalized(OB_FINALIZABLE_SECTION.CLOUD_SENSORS)(store) &&
      !onlyPurchasedAddOnServices(store),
    internetExposure: (store: Store) =>
      isSectionFinalized(OB_FINALIZABLE_SECTION.INTERNET_EXPOSURE)(store) &&
      !onlyPurchasedIRRProducts(store),
    endpoint: (store: Store) =>
      isSectionFinalized(OB_FINALIZABLE_SECTION.ENDPOINT)(store) &&
      !onlyPurchasedAddOnServices(store),
  },
  onlyPurchasedAddOnServices: (store: Store): boolean =>
    onlyPurchasedAddOnServices(store),

  onlyPurchasedIRRProducts: (store: Store): boolean =>
    onlyPurchasedIRRProducts(store),

  getHardwareQuantity: (store: Store): DeviceQuantityMap | undefined =>
    store.onboarding.products?.hardwareQuantity,
};

const getAvailableEndpoints = createSelector(
  [onboardingSelector.getAvailableEndpoints],
  (availableEnpoints) => availableEnpoints,
);

const getAvailableHardware = createSelector(
  [
    onboardingSelector.getAvailableHardware,
    featureFlagSelector.onboarding.scannerPlatformization,
  ],
  (availableHardware, scannerPlatformization) => {
    if (scannerPlatformization) {
      return {
        sensors: [...availableHardware.sensors],
        scanners: [AWN_202_SCANNER_INFO],
      };
    } else {
      return availableHardware;
    }
  },
);

const getSiteReferences = (uuid: string) =>
  createSelector(
    [onboardingSelector.getSensors, onboardingSelector.getScanners],
    (sensors, scanners) => {
      return [...Object.values(sensors), ...Object.values(scanners)].reduce<
        string[]
      >((result, s) => {
        if (s.siteId === uuid || s.shippingSiteId === uuid) {
          result.push(s.name);
        }
        return result;
      }, []);
    },
  );

const getPrimaryMSPContacts = createSelector(
  [onboardingSelector.getContacts],
  (contacts) =>
    Object.values(contacts).filter(
      (c) => c.isMSP && c.contactType === 'primary',
    ),
);

export const memoizedOnboardingSelectors = {
  getAvailableHardware: getAvailableHardware,
  getAvailableEndpoints: getAvailableEndpoints,
  getSiteReferences: getSiteReferences,
  getPrimaryMSPContacts: getPrimaryMSPContacts,
};
